/**
 * Single Run Form
 *
 * Form for starting a single simulation run with enhanced metadata.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  ListSubheader
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Science as SandboxIcon,
  Public as LiveIcon,
  Security as CBCIcon,
  Psychology as ParasocialIcon,
  SmartToy as ModelIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import PersonaPickerModal from '../personaLibrary/PersonaPickerModal';
import {
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  startAdvancedSimulationRun,
  getAdvancedSimulationRun
} from '../../../services/advancedSimulationApi';
import { listSandboxPersonas } from '../../../services/researcherApi';
import { getAvailableLLMModels, getLLMConfiguration } from '../../../services/api';

function SingleRunForm({ token, onRunCompleted, isResearcher = false }) {
  const [liveClientPersonas, setLiveClientPersonas] = useState([]);
  const [liveTherapistPersonas, setLiveTherapistPersonas] = useState([]);
  const [sandboxClientPersonas, setSandboxClientPersonas] = useState([]);
  const [sandboxTherapistPersonas, setSandboxTherapistPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);

  // Persona filter for researchers
  const [personaFilter, setPersonaFilter] = useState('all'); // 'all', 'live', 'sandbox'

  // Library picker (Phase 3) — opens the faceted persona browser as an
  // alternative to the live persona dropdown.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Form state
  const [clientPersonaKey, setClientPersonaKey] = useState('');
  const [therapistPersonaKey, setTherapistPersonaKey] = useState('');
  const [clientPersonaSource, setClientPersonaSource] = useState('live'); // 'live' or 'sandbox'
  const [therapistPersonaSource, setTherapistPersonaSource] = useState('live'); // 'live' or 'sandbox'
  const [maxTurns, setMaxTurns] = useState(50);
  const [tags, setTags] = useState('');

  // CBC (Conversational Boundary Control) options
  const [cbcEnabled, setCbcEnabled] = useState(false);
  const [cbcPreset, setCbcPreset] = useState('moderate');
  const [cbcTransparencyMode, setCbcTransparencyMode] = useState('implicit_then_explicit');
  const [cbcClassificationMethod, setCbcClassificationMethod] = useState('keywords_only');

  // Parasocial Detection options
  const [parasocialEnabled, setParasocialEnabled] = useState(false);
  const [parasocialDisclosureMode, setParasocialDisclosureMode] = useState('moderate');

  // Model Configuration options
  const [usePlatformDefaults, setUsePlatformDefaults] = useState(true);
  const [availableModels, setAvailableModels] = useState({ openai: [], gemini: [], vertex_ai: [] });
  const [platformConfig, setPlatformConfig] = useState(null);
  const [clientModel, setClientModel] = useState('gpt-4o');
  const [clientTemperature, setClientTemperature] = useState(0.7);
  const [therapistProvider, setTherapistProvider] = useState('vertex_ai');
  const [therapistModel, setTherapistModel] = useState('gemini-2.5-flash');
  const [therapistTemperature, setTherapistTemperature] = useState(0.7);

  // Load personas on mount
  useEffect(() => {
    loadPersonas();
  }, [token, isResearcher]);

  // Track if initial load has happened
  const [modelsInitialized, setModelsInitialized] = useState(false);

  // Load available models and platform config
  useEffect(() => {
    loadModelsAndConfig();
  }, [token]);

  const loadModelsAndConfig = async () => {
    try {
      const [modelsRes, configRes] = await Promise.all([
        getAvailableLLMModels(token, { activeOnly: true }),
        getLLMConfiguration(token)
      ]);

      // Group models by provider
      const grouped = { openai: [], gemini: [], vertex_ai: [] };
      (modelsRes.models || []).forEach(model => {
        if (grouped[model.provider]) {
          grouped[model.provider].push(model);
        }
      });
      setAvailableModels(grouped);
      setPlatformConfig(configRes.configuration);

      // Only set defaults on initial load, not when re-fetching
      if (!modelsInitialized && configRes.configuration) {
        const config = configRes.configuration;
        setClientModel(config.simulatorClientModel || 'gpt-4o');
        setClientTemperature(config.simulatorClientTemperature || 0.7);
        setTherapistProvider(config.primaryProvider || 'vertex_ai');
        setTherapistModel(config.primaryModel || 'gemini-2.5-flash');
        setTherapistTemperature(config.defaultTemperature || 0.7);
        setModelsInitialized(true);
      }
    } catch (err) {
      console.error('Failed to load LLM configuration:', err);
      // Continue with hardcoded defaults if API fails
    }
  };

  // Poll for run status when running
  useEffect(() => {
    let interval;
    if (currentRun && currentRun.status === 'running') {
      interval = setInterval(async () => {
        try {
          const updated = await getAdvancedSimulationRun(currentRun._id, token);
          setCurrentRun(updated);
          if (updated.status === 'completed' || updated.status === 'failed') {
            setRunning(false);
            if (updated.status === 'completed') {
              onRunCompleted?.(updated.runId);
            }
          }
        } catch (err) {
          console.error('Error polling run status:', err);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [currentRun, token, onRunCompleted]);

  const loadPersonas = async () => {
    setLoading(true);
    try {
      // Load live personas
      const [clientRes, therapistRes] = await Promise.all([
        getAdvancedSimulationClientPersonas(token),
        getAdvancedSimulationTherapistPersonas(token)
      ]);
      setLiveClientPersonas(clientRes.personas || []);
      setLiveTherapistPersonas(therapistRes.personas || []);

      // Load sandbox personas if researcher
      if (isResearcher) {
        try {
          const sandboxRes = await listSandboxPersonas();
          const clientSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
            .filter(p => p.personaType === 'client');
          const therapistSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
            .filter(p => p.personaType === 'therapist');
          setSandboxClientPersonas(clientSandbox);
          setSandboxTherapistPersonas(therapistSandbox);
        } catch (sandboxErr) {
          console.error('Failed to load sandbox personas:', sandboxErr);
          // Don't fail completely if sandbox load fails
        }
      }
    } catch (err) {
      setError('Failed to load personas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Compute filtered personas based on filter selection
  const getFilteredClientPersonas = () => {
    const live = liveClientPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxClientPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const getFilteredTherapistPersonas = () => {
    const live = liveTherapistPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxTherapistPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const clientPersonas = getFilteredClientPersonas();
  const therapistPersonas = getFilteredTherapistPersonas();

  // Handle persona selection with source tracking
  const handleClientPersonaChange = (key) => {
    setClientPersonaKey(key);
    const persona = clientPersonas.find(p => p.key === key);
    setClientPersonaSource(persona?.source || 'live');
  };

  // Phase 3: library-driven selection. Library only contains live
  // ClientPersona docs, so source is always 'live' when picked this way.
  const handleLibraryPick = (persona) => {
    setClientPersonaKey(persona.key);
    setClientPersonaSource('live');
    setPickerOpen(false);
  };

  // Find the currently-selected client persona object (for the summary chip).
  const selectedClientPersona = clientPersonas.find(p => p.key === clientPersonaKey);

  const handleTherapistPersonaChange = (key) => {
    setTherapistPersonaKey(key);
    const persona = therapistPersonas.find(p => p.key === key);
    setTherapistPersonaSource(persona?.source || 'live');
  };

  const handleStartRun = async () => {
    if (!clientPersonaKey || !therapistPersonaKey) {
      setError('Please select both client and therapist personas');
      return;
    }

    setRunning(true);
    setError(null);
    setCurrentRun(null);

    try {
      const runConfig = {
        clientPersonaKey,
        therapistPersonaKey,
        clientPersonaSource,
        therapistPersonaSource,
        maxTurns: parseInt(maxTurns),
        tags: tags.split(',').map(t => t.trim()).filter(t => t)
      };

      // Add CBC config if enabled
      if (cbcEnabled) {
        runConfig.cbcConfig = {
          enabled: true,
          preset: cbcPreset,
          transparencyMode: cbcTransparencyMode,
          classificationMethod: cbcClassificationMethod
        };
      }

      // Add parasocial config if enabled
      if (parasocialEnabled) {
        runConfig.parasocialConfig = {
          enabled: true,
          disclosureMode: parasocialDisclosureMode
        };
      }

      // Add model configuration (unless using platform defaults)
      if (!usePlatformDefaults) {
        runConfig.clientLlmConfig = {
          provider: 'openai',
          model: clientModel,
          temperature: clientTemperature
        };
        runConfig.therapistLlmConfig = {
          provider: therapistProvider,
          model: therapistModel,
          temperature: therapistTemperature
        };
      }

      const result = await startAdvancedSimulationRun(runConfig, token);

      // Fetch the full run to start polling
      const run = await getAdvancedSimulationRun(result._id, token);
      setCurrentRun(run);
    } catch (err) {
      setError(err.error || 'Failed to start simulation');
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PersonaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleLibraryPick}
        token={token}
        preselectedKey={clientPersonaKey}
      />
      <Typography variant="h6" gutterBottom>
        Run Single Simulation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Run a single simulation with enhanced metadata collection including latency, token counts, and quality metrics.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Persona Filter for Researchers */}
      {isResearcher && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Persona Source Filter
          </Typography>
          <ToggleButtonGroup
            value={personaFilter}
            exclusive
            onChange={(e, value) => value && setPersonaFilter(value)}
            size="small"
          >
            <ToggleButton value="all">
              All Personas
            </ToggleButton>
            <ToggleButton value="sandbox">
              <SandboxIcon sx={{ mr: 0.5 }} fontSize="small" />
              Sandbox Only
            </ToggleButton>
            <ToggleButton value="live">
              <LiveIcon sx={{ mr: 0.5 }} fontSize="small" />
              Live Only
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Sandbox: {sandboxClientPersonas.length} clients, {sandboxTherapistPersonas.length} therapists
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setPickerOpen(true)}
                disabled={running}
              >
                Browse persona library
              </Button>
              <Typography variant="caption" color="text.secondary">
                Faceted + semantic search across the live persona library
              </Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Client Persona</InputLabel>
              <Select
                value={clientPersonaKey}
                onChange={(e) => handleClientPersonaChange(e.target.value)}
                label="Client Persona"
                disabled={running}
              >
                {clientPersonas.map((persona) => (
                  <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {persona.source === 'sandbox' && (
                        <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                      )}
                      {persona.name} ({persona.key})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedClientPersona && (
              <Card variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {selectedClientPersona.personaImage?.url && (
                  <Box
                    component="img"
                    src={selectedClientPersona.personaImage.url}
                    alt={selectedClientPersona.name}
                    sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>{selectedClientPersona.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(selectedClientPersona.metadata?.conditions || []).slice(0, 3).map(c => (
                      <Chip key={c} label={c} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                    ))}
                  </Box>
                </Box>
              </Card>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Therapist Persona</InputLabel>
            <Select
              value={therapistPersonaKey}
              onChange={(e) => handleTherapistPersonaChange(e.target.value)}
              label="Therapist Persona"
              disabled={running}
            >
              {therapistPersonas.map((persona) => (
                <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {persona.source === 'sandbox' && (
                      <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                    )}
                    {persona.name} ({persona.key})
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Max Turns"
            value={maxTurns}
            onChange={(e) => setMaxTurns(e.target.value)}
            inputProps={{ min: 10, max: 200 }}
            helperText="Maximum number of conversation turns (10-200)"
            disabled={running}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            helperText="Optional tags for organization"
            disabled={running}
          />
        </Grid>

        {/* CBC Configuration Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CBCIcon color={cbcEnabled ? 'primary' : 'disabled'} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Conversational Boundary Control (CBC)
                </Typography>
                <Chip
                  label={cbcEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={cbcEnabled ? 'primary' : 'default'}
                  onClick={() => setCbcEnabled(!cbcEnabled)}
                  sx={{ cursor: 'pointer', ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Test the AI therapist's ability to maintain therapeutic boundaries when clients try to redirect the conversation.
              </Typography>

              {cbcEnabled && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>CBC Preset</InputLabel>
                      <Select
                        value={cbcPreset}
                        onChange={(e) => setCbcPreset(e.target.value)}
                        label="CBC Preset"
                        disabled={running}
                      >
                        <MenuItem value="conservative">
                          Conservative (Strict boundaries)
                        </MenuItem>
                        <MenuItem value="moderate">
                          Moderate (Balanced approach)
                        </MenuItem>
                        <MenuItem value="permissive">
                          Permissive (Flexible boundaries)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Transparency Mode</InputLabel>
                      <Select
                        value={cbcTransparencyMode}
                        onChange={(e) => setCbcTransparencyMode(e.target.value)}
                        label="Transparency Mode"
                        disabled={running}
                      >
                        <MenuItem value="implicit_only">
                          Implicit Only
                        </MenuItem>
                        <MenuItem value="implicit_then_explicit">
                          Implicit Then Explicit
                        </MenuItem>
                        <MenuItem value="always_explicit">
                          Always Explicit
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Classification Method</InputLabel>
                      <Select
                        value={cbcClassificationMethod}
                        onChange={(e) => setCbcClassificationMethod(e.target.value)}
                        label="Classification Method"
                        disabled={running}
                      >
                        <MenuItem value="inline_prompt">
                          Inline Prompt (Recommended, zero latency)
                        </MenuItem>
                        <MenuItem value="keywords_only">
                          Keywords Only (Fast, may have false positives)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Parasocial Detection Configuration Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ParasocialIcon color={parasocialEnabled ? 'secondary' : 'disabled'} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Parasocial Detection
                </Typography>
                <Chip
                  label={parasocialEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={parasocialEnabled ? 'secondary' : 'default'}
                  onClick={() => setParasocialEnabled(!parasocialEnabled)}
                  sx={{ cursor: 'pointer', ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Test detection of unhealthy attachment patterns and AI transparency interventions.
              </Typography>

              {parasocialEnabled && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Disclosure Mode</InputLabel>
                      <Select
                        value={parasocialDisclosureMode}
                        onChange={(e) => setParasocialDisclosureMode(e.target.value)}
                        label="Disclosure Mode"
                        disabled={running}
                      >
                        <MenuItem value="conservative">
                          Conservative (Higher thresholds, fewer interventions)
                        </MenuItem>
                        <MenuItem value="moderate">
                          Moderate (Balanced approach)
                        </MenuItem>
                        <MenuItem value="proactive">
                          Proactive (Lower thresholds, early interventions)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Model Configuration Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ModelIcon color={!usePlatformDefaults ? 'info' : 'disabled'} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Model Configuration
                </Typography>
                <Chip
                  label={usePlatformDefaults ? 'Platform Defaults' : 'Custom'}
                  size="small"
                  color={usePlatformDefaults ? 'default' : 'info'}
                  onClick={() => setUsePlatformDefaults(!usePlatformDefaults)}
                  sx={{ cursor: 'pointer', ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {usePlatformDefaults
                  ? 'Using platform default model settings. Click "Platform Defaults" to customize models for this simulation.'
                  : 'Configure which LLM models to use for the simulated client and AI therapist.'}
              </Typography>

              {!usePlatformDefaults && (
                <Grid container spacing={3}>
                  {/* Client (Simulated Patient) Settings */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Client (Simulated Patient) - OpenAI
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Client Model</InputLabel>
                      <Select
                        value={clientModel}
                        onChange={(e) => setClientModel(e.target.value)}
                        label="Client Model"
                        disabled={running}
                      >
                        {availableModels.openai.length > 0 ? (
                          availableModels.openai.map(model => (
                            <MenuItem key={model.id} value={model.id}>
                              {model.displayName || model.id}
                              {model.isDeprecated && ' (Deprecated)'}
                            </MenuItem>
                          ))
                        ) : (
                          <>
                            <MenuItem value="gpt-4o">GPT-4o (Best quality)</MenuItem>
                            <MenuItem value="gpt-4o-mini">GPT-4o Mini (Cost-effective)</MenuItem>
                            <MenuItem value="gpt-4">GPT-4 (Legacy)</MenuItem>
                            <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Budget)</MenuItem>
                          </>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Client Temperature"
                      value={clientTemperature}
                      onChange={(e) => setClientTemperature(parseFloat(e.target.value))}
                      inputProps={{ min: 0, max: 2, step: 0.1 }}
                      helperText="Controls randomness (0=focused, 2=creative)"
                      disabled={running}
                    />
                  </Grid>

                  {/* Therapist (AI Being Tested) Settings */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, color: 'text.secondary' }}>
                      Therapist (AI Being Tested) - Google Gemini
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Provider</InputLabel>
                      <Select
                        value={therapistProvider}
                        onChange={(e) => {
                          setTherapistProvider(e.target.value);
                          // Reset model when provider changes
                          const models = availableModels[e.target.value] || [];
                          if (models.length > 0) {
                            setTherapistModel(models[0].id);
                          } else {
                            setTherapistModel(e.target.value === 'vertex_ai' ? 'gemini-2.5-flash' : 'gemini-2.0-flash');
                          }
                        }}
                        label="Provider"
                        disabled={running}
                      >
                        <MenuItem value="vertex_ai">Vertex AI (Primary)</MenuItem>
                        <MenuItem value="gemini">Gemini API (Fallback)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Therapist Model</InputLabel>
                      <Select
                        value={therapistModel}
                        onChange={(e) => setTherapistModel(e.target.value)}
                        label="Therapist Model"
                        disabled={running}
                      >
                        {(availableModels[therapistProvider] || []).length > 0 ? (
                          availableModels[therapistProvider].map(model => (
                            <MenuItem key={model.id} value={model.id}>
                              {model.displayName || model.id}
                              {model.isDeprecated && ' (Deprecated)'}
                            </MenuItem>
                          ))
                        ) : (
                          <>
                            <MenuItem value="gemini-2.5-flash">Gemini 2.5 Flash</MenuItem>
                            <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash</MenuItem>
                            <MenuItem value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>
                            <MenuItem value="gemini-1.5-flash">Gemini 1.5 Flash</MenuItem>
                          </>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Therapist Temperature"
                      value={therapistTemperature}
                      onChange={(e) => setTherapistTemperature(parseFloat(e.target.value))}
                      inputProps={{ min: 0, max: 2, step: 0.1 }}
                      helperText="Controls randomness"
                      disabled={running}
                    />
                  </Grid>
                </Grid>
              )}

              {usePlatformDefaults && platformConfig && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Current defaults: Client = <strong>{platformConfig.simulatorClientModel || 'gpt-4o'}</strong> |
                    Therapist = <strong>{platformConfig.primaryModel || 'gemini-2.5-flash'}</strong> ({platformConfig.primaryProvider || 'vertex_ai'})
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={running ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
            onClick={handleStartRun}
            disabled={running || !clientPersonaKey || !therapistPersonaKey}
            size="large"
          >
            {running ? 'Running...' : 'Start Simulation'}
          </Button>
        </Grid>
      </Grid>

      {/* Current Run Status */}
      {currentRun && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Run
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={currentRun.status}
                  color={
                    currentRun.status === 'completed' ? 'success' :
                    currentRun.status === 'running' ? 'primary' :
                    currentRun.status === 'failed' ? 'error' : 'default'
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Turns</Typography>
                <Typography variant="body1">{currentRun.turns?.length || 0}</Typography>
              </Grid>
              {currentRun.metrics && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Quality Score</Typography>
                    <Typography variant="body1">{currentRun.metrics.qualityScore || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Est. Cost</Typography>
                    <Typography variant="body1">
                      ${(currentRun.metrics.estimatedCost || 0).toFixed(4)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>

            {currentRun.status === 'completed' && currentRun.turns && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recent Messages
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {currentRun.turns.slice(-6).map((turn, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: turn.actor === 'client' ? 'grey.100' : 'primary.50',
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {turn.actor.toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {turn.content.substring(0, 200)}
                        {turn.content.length > 200 && '...'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default SingleRunForm;
